import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import postedAgo from 'helpers/postedAgo';
import { Link } from 'react-router-dom';
import { Button, Row, Col } from 'react-bootstrap';
import format from 'date-fns/format';

import DatePicker from 'components/Shared/DatePicker';
import Label from 'components/Label/Label';
import Markdown from 'components/Shared/Markdown';
import Panel from 'components/Panel/Panel';
import Price from 'components/Price/Price';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import AlertBox from 'components/AlertBox/AlertBox';
import Modal from 'components/Modal/Modal';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import { mergeDeep } from 'helpers/HelperFunctions';
import {
  getClientActivityLabel,
  getClientActivityStatus,
} from 'helpers/clientActivityStatus';
import ImageCropper from 'components/ImageCropper2/ImageCropper2';
import SingleLocationDropdown from 'components/SingleLocationDropdown/SingleLocationDropdown';
import Icon from 'components/Icon/Icon';
import BrowseCTA from 'components/Shared/Buttons/BrowseCTA/BrowseCTA';
import { TODAY } from 'constants/constants';
import briefShape from 'propTypes/briefShape';
import styles from './BriefDetail.module.scss';
import ReportUserModal from '../../ReportUserModal/ReportUserModal';

const BriefDetail = ({
  brief,
  browseURL,
  editable,
  isOwner,
  isReview,
  narrow,
  onSaveEdit,
  owner,
  user,
}) => {
  const [changes, setChanges] = useState({});
  const [date, setDate] = useState(new Date(brief.expires_at));
  const [editing, setEditing] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState(false);
  const [errors, setErrors] = useState({});
  const [showCurrencyWarning, setShowCurrencyWarning] = useState(false);
  const [tempAvatar, setTempAvatar] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    if (window.location.href.includes('report=1')) {
      setShowReportModal(true);
    }
  }, []);

  const handleShowReportModal = () => {
    setShowReportModal(true);
  };

  const handleChange = (object, field, value) => {
    setChanges((prevChanges) =>
      mergeDeep(prevChanges, { [object]: { [field]: value } })
    );

    // Clear validation
    if (object === 'brief' && field === 'text' && value.length > 2) {
      const newErrors = Object.assign({}, errors);
      delete newErrors.briefTitle;

      setErrors(newErrors);
    }
  };

  const handleLocationRemote = () => {
    const newChanges = {
      brief: {
        remote: 1,
        location_text: '',
      },
    };

    setChanges((prevChanges) => mergeDeep(prevChanges, newChanges));
  };

  const handleChangeLocation = (location) => {
    let newChanges = {};
    if (!location.city || !location.country) {
      newChanges = {
        brief: {
          remote: 1,
          location_text: '',
        },
      };
    } else {
      newChanges = {
        brief: {
          remote: 0,
          location_text:
            location.city && location.country
              ? location.city + ', ' + location.country
              : location.country,
        },
      };
    }

    setChanges((prevChanges) => mergeDeep(prevChanges, newChanges));
  };

  const handleChangeAvatar = (data) => {
    // Avatar is a special case where multiple fields are required to be updated
    const newChanges = {
      user: {
        avatar: data.image,
        crop: {
          avatar: {
            h: data.height,
            w: data.width,
            x: data.x,
            y: data.y,
          },
        },
      },
    };

    setChanges((prevChanges) => mergeDeep(prevChanges, newChanges));
    setEditingAvatar(false);
    setTempAvatar(data.croppedImage);
  };

  const handleSaveEdit = () => {
    // Local validation
    if (changes?.brief?.text?.length < 3) {
      setErrors({ briefTitle: 'The job title must be more than 2 characters' });
      return;
    }

    onSaveEdit(changes, tempAvatar);

    setChanges({});
    setEditing(false);
    setTempAvatar(null);
    setErrors({});
  };

  const activityStatus = getClientActivityStatus(brief, owner);

  const ownerInfo = (
    <div className={styles.clearfix}>
      <div
        className={
          editing
            ? styles.nameLocationWrapperEditing
            : styles.nameLocationWrapper
        }
      >
        <div className={styles.avatarNameWrapper}>
          <div className={styles.avatar}>
            {tempAvatar ? (
              <img
                src={tempAvatar}
                className={`${styles.ownerAvatar} ${styles.tempAvatar}`}
                alt="Twine"
              />
            ) : owner.tempAvatar ? (
              <img
                src={owner.tempAvatar}
                className={styles.tempOwnerAvatar}
                alt="Twine"
              />
            ) : (
              <Thumbnail
                thumbnails={owner.avatars}
                circle
                className={styles.ownerAvatar}
              />
            )}
            {editing && (
              <div
                className={styles.avatarOverlay}
                onClick={() => setEditingAvatar(true)}
              >
                <div />
                <SvgIcon icon="pencil" color="#fff" size="24" />
              </div>
            )}
          </div>
          <div className={styles.displayname}>
            {!editing && (
              <span className={styles.ownerName}>
                <strong>{owner.displayname}</strong>
              </span>
            )}
            {editing && (
              <input
                className="form-control"
                defaultValue={owner.displayname}
                onChange={(event) =>
                  handleChange('user', 'displayname', event.target.value)
                }
                placeholder="Enter a name (eg. My Company Ltd)"
                type="text"
              />
            )}
          </div>
        </div>
      </div>
      {!editing && activityStatus && (
        <span
          className={`${styles.activityPill} ${
            activityStatus === 'recently_active'
              ? styles.activityPillAmber
              : styles.activityPillGreen
          }`}
        >
          {getClientActivityLabel(activityStatus)}
        </span>
      )}
    </div>
  );

  return (
    <Panel
      className={`${styles.container} ${narrow ? styles.narrow : ''}`}
      noPadding={true}
    >
      <Panel.Item>
        {!editing && brief.remote && (
          <h1 title={brief.text + ' – Remote job'}>{brief.text} 🌎</h1>
        )}
        {!editing && !brief.remote && (
          <h1 title={brief.text + ' – ' + brief.location}>
            {brief.text} 📍 {brief.location}
          </h1>
        )}
        {editing && (
          <div>
            <input
              className={`form-control ${styles.editTitle} ${
                typeof errors.briefTitle !== 'undefined'
                  ? styles.errorInput
                  : ''
              }`}
              defaultValue={brief.text}
              onChange={(event) =>
                handleChange('brief', 'text', event.target.value)
              }
              type="text"
              maxLength="140"
            />
            {((typeof changes.brief !== 'undefined' &&
              typeof changes.brief.text !== 'undefined' &&
              changes.brief.text.length > 100) ||
              brief.text.length > 100) && (
              <div>
                <em>
                  Characters left:{' '}
                  {140 -
                    (typeof changes.brief !== 'undefined' &&
                    typeof changes.brief.text !== 'undefined'
                      ? changes.brief.text.length
                      : brief.text.length)}
                </em>
              </div>
            )}
            {typeof errors.briefTitle !== 'undefined' && (
              <div className={styles.errorText}>{errors.briefTitle}</div>
            )}
          </div>
        )}
        <div className={styles.briefInfo}>
          <Label className={styles.roleLabel} color="primary" inverse>
            {brief.role || brief.skill}
          </Label>
          {editing && (
            <div className={styles.editFieldsContainer}>
              <Row>
                <Col xs={12} sm={8} md={4}>
                  <div className={styles.editField}>
                    <label className={styles.fieldLabel}>
                      <Icon
                        style={{ marginRight: '5px' }}
                        icon="&#xe694;"
                        color="#5f5757"
                      />
                      Will your freelancer be required onsite?
                    </label>
                    <SingleLocationDropdown
                      defaultLocation={brief.location}
                      onLocationChange={(location) =>
                        handleChangeLocation(location)
                      }
                      onLocationReset={handleLocationRemote}
                      placeholder="Enter location or leave empty for remote"
                      showResetIcon={true}
                      shouldClearOnBlur={true}
                      shouldResetOnBlur={true}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          )}
          {!editing && brief.amount && brief.amount !== 0 ? (
            <Label className={styles.budgetLabel} color="success" inverse>
              Budget: <Price currency={brief.currency} amount={brief.budget} />
            </Label>
          ) : null}
          {!editing && brief.active && (
            <Label color="darkgrey" className={styles.timeframeLabel}>
              {moment(brief.expires_at).fromNow(true)} left
            </Label>
          )}
          {editing && (
            <div className={styles.editFieldsContainer}>
              <Row>
                <Col xs={12} sm={4} md={2}>
                  <div className={styles.editField}>
                    <label className={styles.fieldLabel}>Budget:</label>
                    <div className={styles.budgetInputWrapper}>
                      <input
                        className="form-control"
                        value={
                          changes.brief &&
                          typeof changes.brief.amount !== 'undefined'
                            ? changes.brief.amount > -1 &&
                              changes.brief.amount !== ''
                              ? changes.brief.amount / 100
                              : ''
                            : brief.budget > -1
                            ? brief.budget / 100
                            : ''
                        }
                        onChange={(event) =>
                          handleChange(
                            'brief',
                            'amount',
                            event.target.value ? event.target.value * 100 : -1
                          )
                        }
                        placeholder="Negotiable"
                        type="number"
                      />
                      <span className={styles.currencySymbol}>
                        {brief.currency === 'usd'
                          ? '$'
                          : brief.currency === 'gbp'
                          ? '£'
                          : '€'}
                      </span>
                    </div>
                    <p
                      className={styles.changeCurrency}
                      onClick={() => setShowCurrencyWarning(true)}
                    >
                      {`Why can't I change the currency?`}
                    </p>
                  </div>
                </Col>
                <Col xs={12} sm={4} md={2}>
                  <div className={styles.editField}>
                    <label className={styles.fieldLabel}>
                      Job closing date:
                    </label>
                    <DatePicker
                      className={`form-control ${styles.datePickerComponent}`}
                      dateFormat="do MMMM"
                      minDate={TODAY}
                      onChange={(newDate) => {
                        setDate(newDate);
                        handleChange(
                          'brief',
                          'expires_at',
                          format(newDate, 'yyyy-MM-dd HH:mm:ss')
                        );
                      }}
                      selected={date}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </Panel.Item>
      {brief.spec !== '' || editing ? (
        <Panel.Item>
          {!editing && (
            <div className={styles.briefDescription}>
              <Markdown text={brief.spec} />
              {isOwner ? (
                <div className={styles.postedAgo}>
                  {postedAgo(brief.timestamp)}
                </div>
              ) : (
                <div className={styles.briefFooter}>
                  <div className={styles.postedAgo}>
                    {postedAgo(brief.approved_at)}
                  </div>
                  {user && (
                    <div className={styles.report}>
                      <Button
                        className="ml"
                        block
                        bsStyle="info"
                        bsSize="sm"
                        onClick={() => {
                          handleShowReportModal();
                        }}
                      >
                        <SvgIcon icon="flag" size="14" /> Report user
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {editing && (
            <div>
              <div className="mb--" style={{ color: '#777777' }}>
                Job description:
              </div>
              <textarea
                className="form-control"
                defaultValue={brief.spec}
                onChange={(event) =>
                  handleChange('brief', 'spec', event.target.value)
                }
                maxLength="10000"
                rows="14"
              />
            </div>
          )}
          {editing &&
            ((typeof changes.brief !== 'undefined' &&
              typeof changes.brief.spec !== 'undefined' &&
              changes.brief.spec.length > 8000) ||
              brief.spec.length > 8000) && (
              <div>
                <em>
                  Characters left:{' '}
                  {10000 -
                    (typeof changes.brief !== 'undefined' &&
                    typeof changes.brief.spec !== 'undefined'
                      ? changes.brief.spec.length
                      : brief.spec.length)}
                </em>
              </div>
            )}
          {editing && (
            <div className="mt" style={{ color: '#777777' }}>
              <input
                defaultChecked={
                  changes.brief && typeof changes.brief.private !== 'undefined'
                    ? changes.brief.private
                    : brief.private
                }
                onChange={(event) =>
                  handleChange('brief', 'private', event.target.checked ? 1 : 0)
                }
                placeholder="Negotiable"
                type="checkbox"
              />{' '}
              Mark job as unlisted.{' '}
              <i>
                If ticked, your job will only be visible to freelancers you
                invite to pitch
              </i>
            </div>
          )}
        </Panel.Item>
      ) : null}
      <Panel.Item className={styles.ownerInfo}>
        {owner.type === 'seller' && !editing ? (
          <Link to={owner.links.main} className={styles.ownerInfoWrapper}>
            {ownerInfo}
          </Link>
        ) : (
          <div className={styles.ownerInfoWrapper}>{ownerInfo}</div>
        )}
      </Panel.Item>
      {editable && (
        <Panel.Item className={styles.editButton}>
          <div
            style={{ justifyContent: isReview ? 'space-between' : 'center' }}
            className={`f f-row ${styles.reviewButtonContainer}`}
          >
            {!editing
              ? [
                  <Button
                    key="edit_button"
                    bsStyle="primary"
                    onClick={() => {
                      scroll({ top: 0, behavior: 'smooth' });
                      setEditing(true);
                    }}
                  >
                    <SvgIcon icon="pencil" size="16" className={styles.icon} />{' '}
                    Edit your brief
                  </Button>,
                  isReview && (
                    <BrowseCTA
                      browseURL={browseURL}
                      title="Next"
                      bsStyle="success"
                    />
                  ),
                ]
              : [
                  <Button
                    key="editing_cancel"
                    bsStyle="info"
                    className={styles.cancelButton}
                    onClick={() => {
                      setChanges({});
                      setEditing(false);
                    }}
                  >
                    <SvgIcon icon="close" size="16" className={styles.icon} />{' '}
                    Cancel
                  </Button>,
                  <Button
                    key="editing_save"
                    bsStyle="success"
                    className={styles.saveButton}
                    onClick={handleSaveEdit}
                  >
                    <SvgIcon icon="tick" size="16" className={styles.icon} />{' '}
                    Save changes
                  </Button>,
                ]}
          </div>
        </Panel.Item>
      )}
      <AlertBox
        show={showCurrencyWarning}
        onConfirm={() => setShowCurrencyWarning(false)}
        title="Why can't I change the currency?"
        text={
          <p>
            Freelancers can only pitch their rate in your chosen currency, and
            so changing it will cause confusion on freelancer rates.
            <br />
            <br />
            If you still need to change it, you can delete the job and post
            again with the correct currency.
          </p>
        }
        icon="info"
        theme="info"
      />
      <Modal
        onExit={() => setEditingAvatar(false)}
        show={editingAvatar}
        closeButton
        title="Change your avatar"
        backgroundExit={false}
      >
        <ImageCropper
          existingImage={owner.avatars}
          onDone={(data) => handleChangeAvatar(data)}
        />
      </Modal>
      <Modal
        show={showReportModal}
        closebutton
        onExit={() => {
          setShowReportModal(false);
        }}
      >
        <ReportUserModal
          accusedUser={owner}
          claimaint={user}
          isBrief={true}
          onClose={() => setShowReportModal(false)}
          brief={brief}
        />
      </Modal>
    </Panel>
  );
};

BriefDetail.propTypes = {
  brief: briefShape,
  browseURL: PropTypes.string,
  editable: PropTypes.bool,
  isOwner: PropTypes.bool,
  isReview: PropTypes.bool,
  narrow: PropTypes.bool,
  onSaveEdit: PropTypes.func,
  owner: PropTypes.shape({
    avatars: PropTypes.object,
    tempAvatar: PropTypes.object,
    displayname: PropTypes.string,
    last_active: PropTypes.string,
    links: PropTypes.shape({
      main: PropTypes.string,
    }),
    loaction: PropTypes.shape({
      text: PropTypes.string,
    }),
    type: PropTypes.oneOf(['seller', 'buyer']),
  }),
  user: PropTypes.object,
};

export default BriefDetail;
